// Stage 2 "Dashboard" · 2.3 — the small building blocks of the firm dashboard: the engagement link, the white panel, the
// table head / cells in the locked colours, and the chips. Colours are explicit hex (see fdTheme.js).
import React from "react";
import { Link } from "react-router-dom";
import { useEngagement } from "@/contexts/EngagementContext";
import { FD } from "./fdTheme";

/** A link that first selects the engagement the way the app does (EngagementContext → lib/activeEngagement: this tab) and then
 *  navigates to the page. Without `engId` it is a plain link. */
export function FdLink({ to, engId, className = "", style, children, testid, title }) {
  const eng = useEngagement();
  const onClick = () => { if (engId && eng && typeof eng.setCurrentEngId === "function") eng.setCurrentEngId(engId); };
  return (
    <Link to={to} onClick={onClick} title={title} data-testid={testid}
          className={`fd-link block no-underline hover:underline ${className}`} style={{ color: FD.link, ...style }}>
      {children}
    </Link>
  );
}

export function Panel({ title, hint, children, testid, right }) {
  return (
    <section data-testid={testid} className="rounded-lg shadow-sm" style={{ background: FD.panel, border: `1px solid ${FD.rowLine}` }}>
      {(title || right) && (
        <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 pt-3 pb-2">
          <div>
            <h2 className="text-base font-semibold" style={{ color: FD.thText }}>{title}</h2>
            {hint ? <div className="text-xs" style={{ color: FD.muted }}>{hint}</div> : null}
          </div>
          {right}
        </div>
      )}
      <div className="px-3 pb-3">{children}</div>
    </section>
  );
}

/** Table in the locked colours: header row #EFECE4 / text #0F4C3A / 2 px #17694F line; cells #F6F4EE; row lines #E2DED3;
 *  fixed layout and wrapping text so the page never scrolls sideways. */
export function FdTable({ cols, children, testid, className = "" }) {
  return (
    <table data-testid={testid} className={`w-full text-sm ${className}`} style={{ tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0 }}>
      {cols ? <colgroup>{cols.map((c) => <col key={c.id} style={{ width: `${c.width}%` }} />)}</colgroup> : null}
      {children}
    </table>
  );
}

export function Th({ children, className = "", align = "start", style }) {
  return (
    <th className={`px-2 py-2 text-${align} text-xs font-semibold uppercase tracking-wide break-words ${className}`}
        style={{ background: FD.thBg, color: FD.thText, borderBottom: `2px solid ${FD.thLine}`, ...style }}>
      {children}
    </th>
  );
}

export function Td({ children, className = "", align = "start", style, testid, colSpan }) {
  return (
    <td data-testid={testid} colSpan={colSpan} className={`px-2 py-2 align-top text-${align} break-words ${className}`}
        style={{ background: FD.cell, color: FD.text, borderBottom: `1px solid ${FD.rowLine}`, ...style }}>
      {children}
    </td>
  );
}

export function Chip({ bg, fg, children, testid, className = "" }) {
  return (
    <span data-testid={testid} className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${className}`}
          style={{ backgroundColor: bg, color: fg }}>
      {children}
    </span>
  );
}

export function Empty({ children }) {
  return <div className="px-2 py-6 text-center text-sm" style={{ color: FD.muted, background: FD.cell }}>{children}</div>;
}
